import React, { forwardRef } from 'react';
import type { TextareaAutosizeProps } from 'react-textarea-autosize';
import TextareaAutosize from 'react-textarea-autosize';
import type { TextAreaProps } from '.';
import { animationTransition } from '../../utils';
import type { ThemeStyledProps } from '@shared/joykit/packages/core/common/styledComponents';
import { styled, css } from '@shared/joykit/packages/core/common/styledComponents';

const TextAreaComponent = forwardRef<HTMLElement, TextAreaProps & Pick<TextareaAutosizeProps, 'onHeightChange'>>(({ minimal, resize, error, suppressFocusRing, ...props }, ref) => {
  return (
    <TextareaAutosize
      ref={ref}
      {...(props as any)}
    />
  );
});

/**
 * The width of the resting hairline. `TextAreaContainer` insets its ring by this much so the ring
 * lands on top of the hairline instead of beside it -- change one and the other follows.
 */
const FIELD_BORDER_WIDTH = '1px';

const baseStyles = css`
  display: block;
  border-radius: ${props => props.theme.radii.field};
  /* Clamps a user-dragged inline width to the wrapper the ring is anchored to. */
  max-width: 100%;
  outline: none;
  width: 100%;
  transition: ${animationTransition('border-color')};
  ${props => props.theme.typography.variants?.body1};
  font-family: ${props => props.theme.typography.primaryFontFamily};
  /*
   * Same one-step divergence InputV1 had: body1 is 15px and every input renders at 16px. Left
   * alone, a textarea would sit a size below the field directly above it in the same form.
   * See doc/field-focus-consistency/PLAN.md.
   */
  font-size: ${props => props.theme.fontSizes[500]};

  :disabled {
    background-color: ${props => props.theme.colors.buttonDisabledFill};
    color: ${props => props.theme.colors.buttonDisabledText};
  }
`;

const plainStyles = (props: ThemeStyledProps<TextAreaProps>) => css`
  ${() => {
    return props.minimal
      ? {
          padding: 0,
          border: 0,
        }
      : css`
          padding: ${props.theme.space[5]};
          /* The resting hairline never changes colour -- hover, focus and invalid are carried by
             the container's ring. See doc/field-focus-consistency/PLAN.md. */
          border: ${FIELD_BORDER_WIDTH} solid ${props.theme.colors.inputBorder};
        `;
  }}
`;

const resizeStyles = (props: TextAreaProps) => css`
  resize: ${() => {
    if (typeof props.resize === 'boolean') {
      // `auto` is not a valid CSS `resize` value; emitting it silently fell through to the UA
      // default. Declare the behaviour instead of inheriting it by accident.
      return props.resize ? 'both' : 'none';
    }
    return props.resize;
  }};
`;

export const StyledTextArea = styled(TextAreaComponent)`
  ${baseStyles}
  ${plainStyles};
  ${resizeStyles}
`;

export const TextAreaContainer = styled.div<Pick<TextAreaProps, 'disabled' | 'error' | 'minimal' | 'suppressFocusRing'> & { focused: boolean }>`
  position: relative;

  ${props => {
    // Gated on `disabled` alone. It also used to skip the ring for `minimal` and for every
    // resizable textarea -- and `resize` defaults to `true` -- which left ~41 textareas in the app
    // with no focus indicator at all.
    if (props.disabled) {
      return null;
    }

    // `minimal` suppresses the field's *border*, not its focus indicator, so the ring survives it.
    // `suppressFocusRing` is the explicit opt-out -- see the matching note on `InputV1`'s
    // `StyledInputContainer` and the entries in doc/field-focus-consistency/EXCEPTIONS.md.
    if (props.suppressFocusRing) {
      return null;
    }

    const { colors, radii } = props.theme;
    const activeColor = props.error ? colors.inputBorderError : colors.inputBorderFocused;
    const isActive = Boolean(props.error) || props.focused;

    // The ring has to overlap the field's hairline, not abut it. This container has no border of its
    // own, so `inset: 0` lands on the textarea's *border box* -- the ring then starts exactly where
    // the hairline ends, and the two rounded rects meeting edge-to-edge leave a pale seam at the
    // corners where their curves disagree by a sub-pixel. Insetting by the border width puts the 2px
    // spread on top of the hairline and 1px past it, which is the footprint `InputV2` gets for free
    // because there the ring and the border share one element. `minimal` has no hairline to cover.
    const ringInset = props.minimal ? '0' : FIELD_BORDER_WIDTH;

    return css`
      &::after {
        content: ' ';
        display: block;
        position: absolute;
        top: ${ringInset};
        left: ${ringInset};
        right: ${ringInset};
        bottom: ${ringInset};
        border-radius: ${radii.field};
        box-shadow: 0 0 0 2px ${isActive ? activeColor : 'transparent'};
        pointer-events: none;
        transition: ${animationTransition('box-shadow')};
      }

      /* Hover and focus share one ring, so hovering a focused field must not demote its colour. */
      &:hover::after {
        box-shadow: 0 0 0 2px ${isActive ? activeColor : colors.mono6};
      }
    `;
  }}
`;
